/* eslint-disable @typescript-eslint/no-explicit-any */

import {FormGroup} from '@angular/forms';
import {IDynamicObject} from '@app/core/models/dynamic-object';
import {ArrayUtils} from '@app/core/utils/array.utils';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {EActionStatus} from '../enums/action-status.enum';
import {IMultiSelectRequest, IMultiSelectState, ISelectState} from '../models/dynamic-form.model';
import {
  EDynamicFormFieldType,
  IDynamicFormCheckbox,
  IDynamicFormFieldType,
  IDynamicFormMultiSelect,
  IDynamicFormOption,
  IDynamicFormSelect,
} from '../models/dynamic.model';

export class DynamicFormFieldUtils {
  static onCheckboxChecked(
    parentGroup: FormGroup,
    formTemplateItem: IDynamicFormCheckbox,
    currentOption: IDynamicFormOption,
    value: string[]
  ) {
    const options = formTemplateItem.options ?? [];
    for (let oindex = 0; oindex < options.length; oindex++) {
      const option = options[`${oindex}`];
      if (option.value === currentOption.value) {
        option.selected = value?.length > 0;
      }
    }
    const currentValue = options.filter(x => x.selected).map(x => x.value);
    parentGroup.controls[formTemplateItem.name].setValue(currentValue);
  }

  static getLabelColClass(formTemplateItem: IDynamicFormFieldType) {
    const colClass = formTemplateItem.labelColSize ? `col-${formTemplateItem.labelColSize}` : 'col-4';
    const styleClasses: IDynamicObject = {};
    styleClasses[`${colClass}`] = !formTemplateItem.labelTop;
    styleClasses['col'] = formTemplateItem.labelTop;
    return styleClasses;
  }

  static getFieldColClass(formTemplateItem: IDynamicFormFieldType) {
    const colClass = formTemplateItem.colSize ? `col-${formTemplateItem.colSize}` : 'col-8';
    const styleClasses: IDynamicObject = {};
    styleClasses[`${colClass}`] = !formTemplateItem.labelTop;
    styleClasses['col'] = formTemplateItem.labelTop;
    styleClasses['align-items-center flex justify-content-end'] =
      formTemplateItem.type === EDynamicFormFieldType.Checkbox ||
      formTemplateItem.type === EDynamicFormFieldType.RadioButton;
    return styleClasses;
  }

  static getSingleOrMultiSelectPlaceholder(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    state: IMultiSelectState | ISelectState
  ) {
    return formTemplateItem.name === state.formControlName &&
      formTemplateItem.lazy &&
      state.status === EActionStatus.Pending
      ? formTemplateItem.loadingPlaceHolder
      : formTemplateItem.placeholder;
  }

  static isSingleOrMultiSelectLoading(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    state: IMultiSelectState | ISelectState
  ) {
    return (
      formTemplateItem.name === state.formControlName && formTemplateItem.lazy && state.status === EActionStatus.Pending
    );
  }

  static loadSingleOrMultiSelectOptions(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    state: IMultiSelectState | ISelectState
  ) {
    let options = formTemplateItem.options ?? [];
    if (
      formTemplateItem.lazy &&
      formTemplateItem.name === state.formControlName &&
      state.status === EActionStatus.Success
    ) {
      let newOptions = [];
      if (formTemplateItem.type === EDynamicFormFieldType.MultiSelect) {
        state = state as IMultiSelectState;
        newOptions = state.successResponse?.terms ?? [];
      } else {
        state = state as ISelectState;
        newOptions = state.successResponse ?? [];
      }
      options = ArrayUtils.uniqueArray([...options, ...newOptions]);
    }
    this.handleShowValueInLabel(formTemplateItem, options);
    formTemplateItem.options = ArrayUtils.uniqueArray(options);
    return formTemplateItem.options;
  }

  static handleShowValueInLabel(formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect, options: any[]) {
    if (options?.length && ObjectUtils.isObject(options[0])) {
      const valueInLabelKey = 'hasValueInLabel';
      const displayLabelKey = 'displayLabel';
      const optionLabelKey = formTemplateItem.optionLabel || 'label';
      const optionValueKey = formTemplateItem.optionValue || 'value';
      for (let i = 0; i < options.length; i++) {
        const option = options[`${i}`];
        if (!option[`${valueInLabelKey}`]) {
          const optionValue = option[`${optionValueKey}`];
          const optionLabel = option[`${optionLabelKey}`];
          if (formTemplateItem.showValueInLabel) {
            option[`${displayLabelKey}`] = `${optionValue} - ${optionLabel}`;
          }
          option[`${valueInLabelKey}`] = formTemplateItem.showValueInLabel ?? false;
        }
        const allowedProperties = [optionValueKey, optionLabelKey, displayLabelKey, valueInLabelKey];
        options[i] = ObjectUtils.keepProperties(option, allowedProperties);
      }
    }
  }

  static getSingleOrMultiSelectPayload(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    pageNumber: number
  ): IMultiSelectRequest {
    const payload: IMultiSelectRequest = {
      page: pageNumber,
      limit: -1,
      q: formTemplateItem.filterValue || formTemplateItem.filterDefaultValue,
      apiEndpointUrl: formTemplateItem.apiEndpointUrl ?? '',
      filterAtClient: formTemplateItem.filterAtClient ?? false,
      excludedOptionLabels: formTemplateItem.optionLabelsExcluded ?? '',
    };
    return payload;
  }
}
